import { useState, useEffect, useCallback, useMemo } from 'react';
import { useOutletContext, useSearchParams } from 'react-router-dom';
import { sb } from '../../lib/supabase';
import useWorkspaceAccess from '../../hooks/useWorkspaceAccess';
import Loader from '../../components/Loader';
import { buildTypePillClass, initials } from '../../lib/workbench';
import {
  completeTestScriptWork,
  approveTestScriptUat,
  fetchOpenTestScriptTasks,
} from '../../lib/tasks';

const TEST_TYPE_LABEL = {
  unit: 'Unit',
  integration: 'Integration',
  uat: 'UAT',
};

const TEST_STATUS_LABEL = {
  draft: 'Draft',
  in_review: 'In review',
  passed: 'Passed',
  approved: 'Approved',
};

function testStatusPillClass(status) {
  if (status === 'passed' || status === 'approved') return 'done';
  if (status === 'in_review') return 'progress';
  return 'pending';
}

export default function TestScripts() {
  const { project } = useOutletContext();
  const { canWrite } = useWorkspaceAccess();
  const [searchParams] = useSearchParams();
  const focusTestId = searchParams.get('focusTest');

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [scripts, setScripts] = useState([]);
  const [openTasks, setOpenTasks] = useState([]);
  const [typeFilter, setTypeFilter] = useState('unit');
  const [statusFilter, setStatusFilter] = useState('all');
  const [toast, setToast] = useState(null);

  const showToast = (msg, type = 'error') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 4000);
  };

  const load = useCallback(async () => {
    if (!project?.id) return;
    setLoading(true);
    try {
      const [scriptRes, taskRows] = await Promise.all([
        sb
          .from('test_scripts')
          .select(`
            id,
            test_type,
            status,
            ref,
            title,
            updated_at,
            test_script_build_items (
              build_item_id,
              deliverables:build_item_id (
                ref,
                title,
                deliverable_types ( name )
              )
            )
          `)
          .eq('project_id', project.id)
          .order('updated_at', { ascending: false }),
        fetchOpenTestScriptTasks(project.id),
      ]);
      setScripts(scriptRes.data || []);
      setOpenTasks(taskRows);
    } catch (e) {
      console.error('[TestScripts] load failed', e);
      showToast('Failed to load test scripts');
    } finally {
      setLoading(false);
    }
  }, [project?.id]);

  useEffect(() => { load(); }, [load]);

  const tasksByScript = useMemo(() => {
    const map = new Map();
    for (const t of openTasks) {
      if (!map.has(t.source_artefact_id)) map.set(t.source_artefact_id, {});
      map.get(t.source_artefact_id)[t.task_type] = t;
    }
    return map;
  }, [openTasks]);

  const filtered = useMemo(() => {
    let rows = scripts;
    if (typeFilter !== 'all') rows = rows.filter((s) => s.test_type === typeFilter);
    if (statusFilter !== 'all') rows = rows.filter((s) => s.status === statusFilter);
    return rows;
  }, [scripts, typeFilter, statusFilter]);

  const stats = useMemo(() => ({
    unit: scripts.filter((s) => s.test_type === 'unit').length,
    open: openTasks.length,
    passed: scripts.filter((s) => s.status === 'passed' || s.status === 'approved').length,
  }), [scripts, openTasks]);

  async function handleCompleteWork(script) {
    const workTask = tasksByScript.get(script.id)?.work;
    if (!workTask) return;
    setSaving(true);
    try {
      await completeTestScriptWork(workTask.id);
      showToast(
        script.test_type === 'uat' ? 'UAT script submitted for BPO sign-off' : 'Test marked passed',
        'success',
      );
      await load();
    } catch (e) {
      showToast(e.message || 'Failed to complete test work');
    } finally {
      setSaving(false);
    }
  }

  async function handleApproveUat(script) {
    const approvalTask = tasksByScript.get(script.id)?.approval;
    if (!approvalTask) return;
    setSaving(true);
    try {
      await approveTestScriptUat(approvalTask.id);
      showToast('UAT signed off', 'success');
      await load();
    } catch (e) {
      showToast(e.message || 'Failed to sign off UAT');
    } finally {
      setSaving(false);
    }
  }

  if (loading) return <Loader text="Loading test scripts…" />;

  return (
    <div className="wb-tracker-shell">
      <div className="wb-review-head">
        <div>
          <div className="wb-review-title">
            Test scripts
            <span className="wb-rex-chip">
              <span className="wb-rex-avatar">R</span>
              Unit owner = builder · UAT sign-off = BPO
            </span>
          </div>
          <div className="wb-review-sub">
            Unit tests auto-create when a build item is marked done. Integration and UAT can be routed manually.
          </div>
        </div>
        <div className="wb-run-progress">
          <div className="wb-run-stat">
            <div className="wb-run-stat-num progress">{stats.unit}</div>
            <div className="wb-run-stat-lbl">Unit scripts</div>
          </div>
          <div className="wb-run-stat">
            <div className="wb-run-stat-num pending">{stats.open}</div>
            <div className="wb-run-stat-lbl">Open tasks</div>
          </div>
          <div className="wb-run-stat">
            <div className="wb-run-stat-num done">{stats.passed}</div>
            <div className="wb-run-stat-lbl">Passed / signed off</div>
          </div>
        </div>
      </div>

      <div className="wb-list-panel">
        <div className="wb-list-toolbar">
          <span className="wb-list-toolbar-label">Type</span>
          {[
            ['unit', 'Unit'],
            ['integration', 'Integration'],
            ['uat', 'UAT'],
            ['all', 'All'],
          ].map(([key, label]) => (
            <button
              key={key}
              type="button"
              className={`wb-filter-pill${typeFilter === key ? ' on' : ''}`}
              onClick={() => setTypeFilter(key)}
            >
              {label}
            </button>
          ))}
          <span className="wb-list-toolbar-label" style={{ marginLeft: 12 }}>Status</span>
          {[
            ['all', 'All'],
            ['draft', 'Draft'],
            ['in_review', 'In review'],
            ['passed', 'Passed'],
            ['approved', 'Approved'],
          ].map(([key, label]) => (
            <button
              key={key}
              type="button"
              className={`wb-filter-pill${statusFilter === key ? ' on' : ''}`}
              onClick={() => setStatusFilter(key)}
            >
              {label}
            </button>
          ))}
        </div>

        <table className="wb-tracker-table">
          <thead>
            <tr>
              <th>Script</th>
              <th>Type</th>
              <th>Build item</th>
              <th>Status</th>
              <th>Assignee</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {filtered.map((script) => {
              const link = script.test_script_build_items?.[0];
              const item = link?.deliverables;
              const typeName = item?.deliverable_types?.name || '—';
              const tasks = tasksByScript.get(script.id) || {};
              const activeTask = tasks.work || tasks.approval;
              const assignee = activeTask?.profiles?.full_name;
              const focused = focusTestId === script.id;

              return (
                <tr key={script.id} className={focused ? 'task-focus' : ''}>
                  <td>
                    <div className="wb-item-ref">{script.ref || '—'}</div>
                    <div className="wb-item-title">{script.title || '—'}</div>
                  </td>
                  <td>{TEST_TYPE_LABEL[script.test_type] || script.test_type}</td>
                  <td>
                    <div className="wb-item-ref">{item?.ref || '—'}</div>
                    <span className={`wb-type-pill ${buildTypePillClass(typeName)}`}>{typeName}</span>
                  </td>
                  <td>
                    <span className={`wb-status-pill ${testStatusPillClass(script.status)}`}>
                      {TEST_STATUS_LABEL[script.status] || script.status}
                    </span>
                  </td>
                  <td>
                    {assignee ? (
                      <div className="wb-owner-cell">
                        <div className="wb-owner-av">{initials(assignee)}</div>
                        {assignee}
                        <span className="wb-owner-muted"> · {activeTask.task_type}</span>
                      </div>
                    ) : (
                      <span className="wb-owner-muted">—</span>
                    )}
                  </td>
                  <td>
                    {canWrite && script.status === 'draft' && tasks.work && (
                      <button
                        type="button"
                        className="wb-row-action-btn primary"
                        disabled={saving}
                        onClick={() => handleCompleteWork(script)}
                      >
                        {script.test_type === 'uat' ? 'Submit script' : 'Mark passed'}
                      </button>
                    )}
                    {canWrite && script.status === 'in_review' && tasks.approval && (
                      <button
                        type="button"
                        className="wb-row-action-btn primary"
                        disabled={saving}
                        onClick={() => handleApproveUat(script)}
                      >
                        BPO sign-off
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
            {!filtered.length && (
              <tr>
                <td colSpan={6} className="wb-table-empty">
                  {scripts.length
                    ? 'No scripts match the current filters.'
                    : 'Mark a build item done in the tracker to seed its unit test script.'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {toast && (
        <div className={`toast show ${toast.type === 'success' ? 'success' : ''}`}>{toast.msg}</div>
      )}
    </div>
  );
}
